import { useTranslation } from "react-i18next";
import { ArrowDown, ArrowUp, FileDown, Minus, TrendingUp, Users, Package } from "lucide-react";

import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import type { SalesReportData } from "@/services/reportService";
import { cn } from "@/lib/utils";
import { formatDisplayDate } from "@/lib/dateFormat";

function fmt(amount: number, currency?: string): string {
  const formatted = amount.toLocaleString("es-UY", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  return currency ? `${currency} ${formatted}` : formatted;
}

function DiffIndicator({ current, previous, label, previousFormatted }: { current: number; previous: number; label: string; previousFormatted?: string }): JSX.Element | null {
  if (previous === 0 && current === 0) return null;

  const tooltipBody = (
    <div className="space-y-1">
      <p className="font-medium">{label}</p>
      {previousFormatted && <p className="text-xs text-muted-foreground">{previousFormatted}</p>}
    </div>
  );

  if (previous === 0) return (
    <Tooltip>
      <TooltipTrigger asChild>
        <span className="inline-flex cursor-help items-center gap-0.5 text-xs font-medium text-green-600 dark:text-green-400"><ArrowUp className="h-3 w-3" /></span>
      </TooltipTrigger>
      <TooltipContent>{tooltipBody}</TooltipContent>
    </Tooltip>
  );
  const pct = Math.round(((current - previous) / Math.abs(previous)) * 100);
  if (pct === 0) return (
    <Tooltip>
      <TooltipTrigger asChild>
        <span className="inline-flex cursor-help items-center gap-0.5 text-xs font-medium text-muted-foreground"><Minus className="h-3 w-3" />0%</span>
      </TooltipTrigger>
      <TooltipContent>{tooltipBody}</TooltipContent>
    </Tooltip>
  );
  const up = pct > 0;
  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <span className={cn("inline-flex cursor-help items-center gap-0.5 text-xs font-medium", up ? "text-green-600 dark:text-green-400" : "text-red-600 dark:text-red-400")}>
          {up ? <ArrowUp className="h-3 w-3" /> : <ArrowDown className="h-3 w-3" />}{Math.abs(pct)}%
        </span>
      </TooltipTrigger>
      <TooltipContent>{tooltipBody}</TooltipContent>
    </Tooltip>
  );
}

function MetricBox({ label, value, subValue, diff }: { label: string; value: string; subValue?: string; diff?: JSX.Element | null }): JSX.Element {
  return (
    <div className="rounded-lg border p-4">
      <p className="text-sm text-muted-foreground">{label}</p>
      <div className="mt-1 flex items-baseline gap-2">
        <span className="text-2xl font-semibold tabular-nums">{value}</span>
        {diff}
      </div>
      {subValue && <p className="mt-0.5 text-xs text-muted-foreground">{subValue}</p>}
    </div>
  );
}

const MONTH_SHORT: Record<string, string> = {
  "01": "Ene", "02": "Feb", "03": "Mar", "04": "Abr", "05": "May", "06": "Jun",
  "07": "Jul", "08": "Ago", "09": "Sep", "10": "Oct", "11": "Nov", "12": "Dic",
};

interface SalesReportCardProps {
  data: SalesReportData;
  onDownloadCsv: () => void;
  csvLoading: boolean;
}

export function SalesReportCard({ data, onDownloadCsv, csvLoading }: SalesReportCardProps): JSX.Element {
  const { t, i18n } = useTranslation();
  const { summary, previousPeriodSummary: prev, revenueBreakdown: rev, clientMetrics, byCfeType, byClient, topProducts, monthlyBreakdown } = data;

  const periodLabel = (() => {
    const from = formatDisplayDate(data.dateFrom, { locale: i18n.language, useCalendarDate: true });
    const to = formatDisplayDate(data.dateTo, { locale: i18n.language, useCalendarDate: true });
    return from === to ? from : `${from} — ${to}`;
  })();

  const prevPeriodLabel = (() => {
    if (!data.previousPeriodDateFrom || !data.previousPeriodDateTo) return null;
    const pFrom = formatDisplayDate(data.previousPeriodDateFrom, { locale: i18n.language, useCalendarDate: true });
    const pTo = formatDisplayDate(data.previousPeriodDateTo, { locale: i18n.language, useCalendarDate: true });
    return pFrom === pTo ? pFrom : `${pFrom} — ${pTo}`;
  })();
  const diffLabel = prevPeriodLabel
    ? t("reports.sales.vsPreviousPeriodDates", "Comparado con {{period}}", { period: prevPeriodLabel })
    : t("reports.sales.vsPreviousPeriod", "Comparado con el período anterior");

  const isEmpty = summary.invoiceCount === 0 && rev.baseInvoiceCount === 0 && rev.creditNoteCount === 0;

  if (isEmpty) {
    return (
      <div className="space-y-4">
        <p className="text-sm text-muted-foreground">{periodLabel}</p>
        <Card>
          <CardContent className="flex flex-col items-center justify-center py-16 text-center">
            <Package className="mb-3 h-10 w-10 text-muted-foreground/40" />
            <p className="text-lg font-medium text-foreground">
              {t("reports.sales.emptyTitle", "Sin actividad en este período")}
            </p>
            <p className="mt-1 max-w-md text-sm text-muted-foreground">
              {t("reports.sales.emptyDescription", "No se encontraron facturas confirmadas por la DGI en el rango seleccionado. Probá con otro período o verificá que las facturas hayan sido enviadas.")}
            </p>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <p className="text-sm text-muted-foreground">{periodLabel}</p>

      {/* Summary KPIs */}
      <Card>
        <CardHeader className="flex flex-row items-center justify-between">
          <CardTitle>{t("reports.sales.summary", "Resumen de ventas")}</CardTitle>
          <Button variant="outline" size="sm" onClick={onDownloadCsv} disabled={csvLoading} className="gap-1.5">
            <FileDown className="h-4 w-4" />
            {t("reports.sales.downloadCsv", "Descargar CSV")}
          </Button>
        </CardHeader>
        <CardContent>
          <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
            <MetricBox
              label={t("reports.sales.invoiceCount", "Facturas")}
              value={String(rev.baseInvoiceCount)}
              diff={prev ? <DiffIndicator current={summary.invoiceCount} previous={prev.invoiceCount} label={diffLabel} previousFormatted={`${t("reports.sales.previousValue", "Anterior")}: ${prev.invoiceCount}`} /> : null}
            />
            {(rev.netUyu !== 0 || rev.grossUyu !== 0) && (
              <MetricBox
                label={t("reports.sales.netRevenueUyu", "Ventas netas UYU")}
                value={fmt(rev.netUyu, "UYU")}
                diff={prev ? <DiffIndicator current={rev.netUyu} previous={prev.totalUyu} label={diffLabel} previousFormatted={`${t("reports.sales.previousValue", "Anterior")}: ${fmt(prev.totalUyu, "UYU")}`} /> : null}
              />
            )}
            {(rev.netUsd !== 0 || rev.grossUsd !== 0) && (
              <MetricBox
                label={t("reports.sales.netRevenueUsd", "Ventas netas USD")}
                value={fmt(rev.netUsd, "USD")}
                diff={prev ? <DiffIndicator current={rev.netUsd} previous={prev.totalUsd} label={diffLabel} previousFormatted={`${t("reports.sales.previousValue", "Anterior")}: ${fmt(prev.totalUsd, "USD")}`} /> : null}
              />
            )}
            {rev.avgTicketUsd > 0 && (
              <MetricBox label={t("reports.sales.avgTicket", "Ticket promedio")} value={fmt(rev.avgTicketUsd, "USD")} />
            )}
            {rev.avgTicketUyu > 0 && rev.avgTicketUsd === 0 && (
              <MetricBox label={t("reports.sales.avgTicket", "Ticket promedio")} value={fmt(rev.avgTicketUyu, "UYU")} />
            )}
          </div>
        </CardContent>
      </Card>

      {/* Revenue breakdown + Client metrics */}
      <div className="grid gap-4 lg:grid-cols-2">
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-base">
              <TrendingUp className="h-4 w-4 text-primary" />
              {t("reports.sales.revenueBreakdown", "Desglose de ingresos")}
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-2 text-sm">
            {rev.grossUsd > 0 && (
              <div className="space-y-1">
                <div className="flex justify-between"><span className="text-muted-foreground">{t("reports.sales.grossSales", "Ventas brutas")}</span><span className="font-medium tabular-nums">{fmt(rev.grossUsd, "USD")}</span></div>
                {rev.creditNotesUsd > 0 && <div className="flex justify-between"><span className="text-muted-foreground">{t("reports.sales.creditNotes", "Notas de crédito")}</span><span className="font-medium tabular-nums text-red-600 dark:text-red-400">-{fmt(rev.creditNotesUsd, "USD")}</span></div>}
                <div className="flex justify-between border-t pt-1 font-semibold"><span>{t("reports.sales.netRevenue", "Ventas netas")}</span><span className="tabular-nums">{fmt(rev.netUsd, "USD")}</span></div>
                <div className="flex justify-between text-xs text-muted-foreground"><span>{t("reports.sales.subtotal", "Neto (sin IVA)")}</span><span className="tabular-nums">{fmt(rev.subtotalUsd)}</span></div>
                <div className="flex justify-between text-xs text-muted-foreground"><span>IVA</span><span className="tabular-nums">{fmt(rev.ivaUsd)}</span></div>
              </div>
            )}
            {rev.grossUyu > 0 && rev.grossUsd > 0 && <hr className="my-2" />}
            {rev.grossUyu > 0 && (
              <div className="space-y-1">
                <div className="flex justify-between"><span className="text-muted-foreground">{t("reports.sales.grossSales", "Ventas brutas")}</span><span className="font-medium tabular-nums">{fmt(rev.grossUyu, "UYU")}</span></div>
                {rev.creditNotesUyu > 0 && <div className="flex justify-between"><span className="text-muted-foreground">{t("reports.sales.creditNotes", "Notas de crédito")}</span><span className="font-medium tabular-nums text-red-600 dark:text-red-400">-{fmt(rev.creditNotesUyu, "UYU")}</span></div>}
                <div className="flex justify-between border-t pt-1 font-semibold"><span>{t("reports.sales.netRevenue", "Ventas netas")}</span><span className="tabular-nums">{fmt(rev.netUyu, "UYU")}</span></div>
                <div className="flex justify-between text-xs text-muted-foreground"><span>{t("reports.sales.subtotal", "Neto (sin IVA)")}</span><span className="tabular-nums">{fmt(rev.subtotalUyu)}</span></div>
                <div className="flex justify-between text-xs text-muted-foreground"><span>IVA</span><span className="tabular-nums">{fmt(rev.ivaUyu)}</span></div>
              </div>
            )}
            {rev.creditNoteRatio > 0 && (
              <p className="mt-2 text-xs text-muted-foreground">
                {t("reports.sales.creditNoteRatioLabel", "Ratio NC")}: {rev.creditNoteRatio.toFixed(1)}% ({rev.creditNoteCount} NC / {rev.baseInvoiceCount + rev.creditNoteCount} docs)
              </p>
            )}
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-base">
              <Users className="h-4 w-4 text-primary" />
              {t("reports.sales.clientMetrics", "Clientes")}
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid gap-3 sm:grid-cols-3">
              <div className="rounded-lg border p-3 text-center">
                <p className="text-2xl font-semibold">{clientMetrics.totalClients}</p>
                <p className="text-xs text-muted-foreground">{t("reports.sales.totalClients", "Total")}</p>
              </div>
              <div className="rounded-lg border p-3 text-center">
                <p className="text-2xl font-semibold text-green-600 dark:text-green-400">{clientMetrics.newClients}</p>
                <p className="text-xs text-muted-foreground">{t("reports.sales.newClients", "Nuevos")}</p>
              </div>
              <div className="rounded-lg border p-3 text-center">
                <p className="text-2xl font-semibold">{clientMetrics.returningClients}</p>
                <p className="text-xs text-muted-foreground">{t("reports.sales.returningClients", "Recurrentes")}</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Monthly breakdown */}
      {monthlyBreakdown.length >= 2 && (
        <Card>
          <CardHeader><CardTitle className="text-base">{t("reports.sales.monthlyTrend", "Desglose mensual")}</CardTitle></CardHeader>
          <CardContent>
            <Table>
              <TableHeader><TableRow>
                <TableHead>{t("reports.sales.monthColumn", "Mes")}</TableHead>
                <TableHead className="text-right">{t("reports.sales.count", "Cantidad")}</TableHead>
                <TableHead className="text-right">UYU</TableHead>
                <TableHead className="text-right">USD</TableHead>
              </TableRow></TableHeader>
              <TableBody>
                {monthlyBreakdown.map((d) => (
                  <TableRow key={d.month}>
                    <TableCell className="font-medium">{MONTH_SHORT[d.month.slice(5)] ?? d.month.slice(5)} {d.month.slice(0, 4)}</TableCell>
                    <TableCell className="text-right tabular-nums">{d.invoiceCount}</TableCell>
                    <TableCell className="text-right tabular-nums">{d.totalUyu !== 0 ? fmt(d.totalUyu) : "—"}</TableCell>
                    <TableCell className="text-right tabular-nums">{d.totalUsd !== 0 ? fmt(d.totalUsd) : "—"}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </CardContent>
        </Card>
      )}

      {/* Top products */}
      {topProducts.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-base">
              <Package className="h-4 w-4 text-primary" />
              {t("reports.sales.topProducts", "Productos / servicios más vendidos")}
            </CardTitle>
          </CardHeader>
          <CardContent>
            <Table>
              <TableHeader><TableRow>
                <TableHead>{t("reports.sales.productDescription", "Descripción")}</TableHead>
                <TableHead className="text-right">{t("reports.sales.qty", "Cant.")}</TableHead>
                <TableHead className="text-right">UYU</TableHead>
                <TableHead className="text-right">USD</TableHead>
              </TableRow></TableHeader>
              <TableBody>
                {topProducts.map((p, i) => (
                  <TableRow key={i}>
                    <TableCell className="max-w-[300px] truncate font-medium">{p.description}</TableCell>
                    <TableCell className="text-right tabular-nums">{Math.round(p.quantity)}</TableCell>
                    <TableCell className="text-right tabular-nums">{p.totalUyu > 0 ? fmt(p.totalUyu) : "—"}</TableCell>
                    <TableCell className="text-right tabular-nums">{p.totalUsd > 0 ? fmt(p.totalUsd) : "—"}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </CardContent>
        </Card>
      )}

      {/* By CFE type */}
      {byCfeType.length > 0 && (
        <Card>
          <CardHeader><CardTitle className="text-base">{t("reports.sales.byCfeType", "Por tipo de comprobante")}</CardTitle></CardHeader>
          <CardContent>
            <Table>
              <TableHeader><TableRow>
                <TableHead>{t("reports.sales.cfeType", "Tipo")}</TableHead>
                <TableHead className="text-right">{t("reports.sales.count", "Cantidad")}</TableHead>
                <TableHead className="text-right">UYU</TableHead>
                <TableHead className="text-right">USD</TableHead>
              </TableRow></TableHeader>
              <TableBody>
                {byCfeType.map((row) => (
                  <TableRow key={row.cfeType}>
                    <TableCell>{row.cfeTypeLabel}</TableCell>
                    <TableCell className="text-right tabular-nums">{row.invoiceCount}</TableCell>
                    <TableCell className="text-right tabular-nums">{row.totalUyu > 0 ? fmt(row.totalUyu) : "—"}</TableCell>
                    <TableCell className="text-right tabular-nums">{row.totalUsd > 0 ? fmt(row.totalUsd) : "—"}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </CardContent>
        </Card>
      )}

      {/* By client */}
      {byClient.length > 0 && (
        <Card>
          <CardHeader><CardTitle className="text-base">{t("reports.sales.byClient", "Por cliente")}</CardTitle></CardHeader>
          <CardContent>
            <Table>
              <TableHeader><TableRow>
                <TableHead>{t("reports.sales.client", "Cliente")}</TableHead>
                <TableHead>{t("reports.sales.rutEin", "RUT / EIN")}</TableHead>
                <TableHead className="text-right">{t("reports.sales.count", "Cantidad")}</TableHead>
                <TableHead className="text-right">UYU</TableHead>
                <TableHead className="text-right">USD</TableHead>
              </TableRow></TableHeader>
              <TableBody>
                {byClient.slice(0, 15).map((row, i) => {
                  const displayName = row.clientDisplayName?.trim() || row.clientName;
                  const docId = (row.clientRut && row.clientRut !== "000000000000") ? row.clientRut : row.clientDocRecepExt?.trim() || null;
                  return (
                    <TableRow key={i}>
                      <TableCell className="font-medium">{displayName}</TableCell>
                      <TableCell className="text-muted-foreground tabular-nums">{docId ?? "—"}</TableCell>
                      <TableCell className="text-right tabular-nums">{row.invoiceCount}</TableCell>
                      <TableCell className="text-right tabular-nums">{row.totalUyu !== 0 ? fmt(row.totalUyu) : "—"}</TableCell>
                      <TableCell className="text-right tabular-nums">{row.totalUsd !== 0 ? fmt(row.totalUsd) : "—"}</TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
